/**
 * Web Notification utilities — handles browser Notification API and
 * service worker registration for web push.
 *
 * These are ONLY called when Platform.OS === 'web'. The main notifications.ts
 * delegates to these for web-specific operations.
 */

import type { User } from '../types/api';

/** Register the service worker and return the push subscription JSON (for server storage) */
export async function registerWebPush(vapidPublicKey: string): Promise<string | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // Check existing subscription
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
    return JSON.stringify(sub.toJSON());
  } catch (err) {
    console.warn('[WebNotif] Failed to register web push:', err);
    return null;
  }
}

/** Request browser notification permission. Returns true if granted. */
export async function requestWebPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const result = await Notification.requestPermission();
  return result === 'granted';
}

/** Show an immediate browser notification */
export function showWebNotification(title: string, body: string, data?: Record<string, unknown>): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  try {
    new Notification(title, {
      body,
      icon: '/assets/icon.png',
      tag: `reminder-${data?.seq || Date.now()}`,
      renotify: true,
      data,
    } as NotificationOptions);
  } catch {
    // Fallback: use service worker notification if available
    navigator.serviceWorker?.ready?.then((reg) => {
      reg.showNotification(title, {
        body,
        icon: '/assets/icon.png',
        tag: `reminder-${data?.seq || Date.now()}`,
      });
    });
  }
}

/** Convert VAPID key from base64url to Uint8Array */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray as Uint8Array<ArrayBuffer>;
}
